import { useEffect, useMemo, useState } from "react";
import type {
  BouquetBuilderItem,
  Color,
  PaperRollRow,
  SubCategory,
} from "../../api/types";
import {
  BUILDER_TABS,
  PAPER_PRESETS,
  eur,
} from "../bouquet/builderShared";
import { t as translate } from "../../i18n/t";
import { ItemThumb } from "../ItemThumb";
import {
  aggregateRolls,
  cartIsEmpty,
  cartStemCount,
  cartSuggestedPrice,
  flowerLineFromCard,
  type ComposerCart,
  type RollItemCard,
  type StockShortage,
} from "./orderBuild";

interface Props {
  flowers: BouquetBuilderItem[];
  colors: Color[];
  subcategories: SubCategory[];
  rolls: PaperRollRow[];
  cart: ComposerCart;
  onCartChange(next: ComposerCart): void;
  /** Per-item shortages from a rejected submit; badges the offending rows. */
  shortages: StockShortage[] | null;
  referencePreviewUrl: string | null;
  onReferenceFile(file: File): void;
  onRemoveReference(): void;
  onSkip(): void;
  onContinue(): void;
}

/**
 * G-93: paper and ribbon are separate lines on the bouquet — most bouquets
 * carry both, and some carry two or three of one. The cart has always held
 * them as a list (`cart.papers`) and the API has always taken an array of
 * cuts; only the screen insisted on one at a time.
 */
const WRAP_KINDS: ReadonlyArray<{ code: string; one: string }> = [
  { code: "PAPER", one: "paper" },
  { code: "RIBBON", one: "ribbon" },
];

/**
 * Step 1 of the phone "Build & send order" flow: tap flower cards into a cart,
 * cut ribbon/paper by length, name the bouquet. Same catalog rules as the
 * shop-floor tablet builder (Flower/Ribbon/Paper only, stock-capped), laid out
 * for a ~375px viewport with a sticky cart bar.
 */
export function PhoneBouquetComposer({
  flowers,
  colors,
  subcategories,
  rolls,
  cart,
  onCartChange,
  shortages,
  referencePreviewUrl,
  onReferenceFile,
  onRemoveReference,
  onSkip,
  onContinue,
}: Props) {
  const [activeTab, setActiveTab] = useState("FLOWER");
  const [colorId, setColorId] = useState("");
  const [subCategoryId, setSubCategoryId] = useState("");
  const [search, setSearch] = useState("");
  /*
   * G-93: the sheet now holds the SHELF, not the cart — the cart is the page.
   * `pickerKind` is the tab the sheet opens on: flowers, or one of the wrap
   * categories. `customFor` is the wrap row whose length is being typed.
   */
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerKind, setPickerKind] = useState("FLOWER");
  const [customFor, setCustomFor] = useState<string | null>(null);
  const [customCm, setCustomCm] = useState("40");

  const rollCards = useMemo(() => aggregateRolls(rolls), [rolls]);

  const tabs = useMemo(() => {
    const present = new Set<string>();
    if (flowers.length > 0) present.add("FLOWER");
    for (const r of rollCards) present.add(r.categoryCode);
    return BUILDER_TABS.filter((code) => present.has(code));
  }, [flowers, rollCards]);

  useEffect(() => {
    if (tabs.length > 0 && !tabs.includes(activeTab)) setActiveTab(tabs[0]);
  }, [tabs, activeTab]);

  const visibleFlowers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return flowers.filter((it) => {
      if (colorId && it.color?.id !== colorId) return false;
      if (subCategoryId && it.subCategory?.id !== subCategoryId) return false;
      if (q && !it.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [flowers, colorId, subCategoryId, search]);

  const visibleRolls = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rollCards.filter((r) => {
      if (r.categoryCode !== activeTab) return false;
      if (colorId && r.color?.id !== colorId) return false;
      if (q && !r.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rollCards, activeTab, colorId, search]);

  const shortageIds = useMemo(
    () => new Set((shortages ?? []).map((s) => s.itemId)),
    [shortages],
  );

  const qtyInCart = (itemId: string) =>
    cart.flowers.find((f) => f.itemId === itemId)?.qty ?? 0;

  const addFlower = (it: BouquetBuilderItem) => {
    const existing = cart.flowers.find((f) => f.itemId === it.itemId);
    if (existing) {
      if (existing.qty >= existing.maxQty) return;
      onCartChange({
        ...cart,
        flowers: cart.flowers.map((f) =>
          f.itemId === it.itemId ? { ...f, qty: f.qty + 1 } : f,
        ),
      });
      return;
    }
    if (Number(it.qtyOnHand) <= 0) return;
    onCartChange({ ...cart, flowers: [...cart.flowers, flowerLineFromCard(it)] });
  };

  const stepFlower = (itemId: string, delta: number) => {
    const next = cart.flowers
      .map((f) =>
        f.itemId === itemId
          ? { ...f, qty: Math.min(f.maxQty, Math.max(0, f.qty + delta)) }
          : f,
      )
      .filter((f) => f.qty > 0);
    onCartChange({ ...cart, flowers: next });
  };

  const setPaper = (roll: RollItemCard, cm: number) => {
    const capped = Math.min(cm, roll.availableCm);
    if (!Number.isFinite(capped) || capped <= 0) return;
    const existing = cart.papers.find((p) => p.itemId === roll.itemId);
    const line = {
      itemId: roll.itemId,
      name: roll.name,
      categoryLabel: roll.categoryLabel,
      cm: capped,
      availableCm: roll.availableCm,
    };
    onCartChange({
      ...cart,
      papers: existing
        ? cart.papers.map((p) => (p.itemId === roll.itemId ? line : p))
        : [...cart.papers, line],
    });
  };

  const removePaper = (itemId: string) =>
    onCartChange({
      ...cart,
      papers: cart.papers.filter((p) => p.itemId !== itemId),
    });

  const suggested = cartSuggestedPrice(cart);
  const stems = cartStemCount(cart);
  const pieces = cart.flowers.length + cart.papers.length;
  const empty = cartIsEmpty(cart);

  /* ── G-93 ────────────────────────────────────────────────────────────────
   * The bouquet you are building leads; the shelf is behind a button.
   *
   * It used to be the other way round: the catalogue filled the screen and the
   * cart hid behind a "Cart" button in the bottom bar, so the thing you were
   * making was the one thing you could not see. The rows, steppers and stock
   * caps below are the same ones that were inside that sheet — only their
   * place on the screen changed.
   * ────────────────────────────────────────────────────────────────────────── */

  const rollById = (itemId: string) => rollCards.find((r) => r.itemId === itemId);

  const wrapRows = cart.papers;
  const wrapSummary = (() => {
    const counts = new Map<string, number>();
    for (const p of wrapRows) counts.set(p.categoryLabel, (counts.get(p.categoryLabel) ?? 0) + 1);
    const parts = [...counts.entries()].map(([label, n]) => `${n} ${label.toLowerCase()}`);
    return parts.length ? parts.join(" + ") : "none";
  })();

  function openPicker(kind: string) {
    setPickerKind(kind);
    setActiveTab(kind);
    setSearch("");
    setPickerOpen(true);
  }

  function addRoll(roll: RollItemCard) {
    // A new cut starts at the middle preset, capped by the roll; the chips on
    // its row change it afterwards, so nothing needs a second screen.
    const preset = PAPER_PRESETS.find((p) => p.value === "MEDIUM") ?? PAPER_PRESETS[0];
    setPaper(roll, Math.min(preset.cm, roll.availableCm));
    setPickerOpen(false);
  }

  return (
    <div className="pob-composer">
      <div className="card pob-reference-card">
        <div>
          <strong>Reference photo (optional)</strong>
          <p className="muted" style={{ margin: 0 }}>
            Add the style or arrangement the florist should follow.
          </p>
        </div>
        {referencePreviewUrl ? (
          <img
            src={referencePreviewUrl}
            alt="Bouquet reference"
            className="pob-reference-preview"
          />
        ) : null}
        <div className="button-row">
          <label className="button tinted">
            {referencePreviewUrl ? "Replace photo" : "Upload photo"}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) onReferenceFile(file);
                event.currentTarget.value = "";
              }}
            />
          </label>
          {referencePreviewUrl ? (
            <button type="button" className="button plain" onClick={onRemoveReference}>
              Remove photo
            </button>
          ) : null}
        </div>
      </div>

      <section className="pob-built">
        <div className="pob-built-head">
          <span>The bouquet</span>
          <span>
            {empty
              ? "empty"
              : `${stems} ${stems === 1 ? "stem" : "stems"} · ${pieces} ${
                  pieces === 1 ? "piece" : "pieces"
                }`}
          </span>
        </div>

        {cart.flowers.length === 0 ? (
          <p className="muted pob-built-empty">
            No flowers yet. Add them from the shelf.
          </p>
        ) : (
          <ul className="pob-cart-list">
            {cart.flowers.map((f) => (
              <li key={f.itemId} className="pob-cart-row">
                <div className="pob-cart-row-main">
                  <span className="pob-cart-row-name">
                    {f.name}
                    {shortageIds.has(f.itemId) ? (
                      <span className="badge danger pob-cart-badge">short</span>
                    ) : null}
                  </span>
                  <span className="muted">
                    {f.unitSalePrice != null
                      ? `${eur(f.unitSalePrice)}/stem`
                      : "price missing"}
                  </span>
                </div>
                <div className="pob-stepper">
                  <button
                    type="button"
                    className="button tinted"
                    aria-label={`Remove one ${f.name}`}
                    onClick={() => stepFlower(f.itemId, -1)}
                  >
                    −
                  </button>
                  <span className="pob-stepper-qty">{f.qty}</span>
                  <button
                    type="button"
                    className="button tinted"
                    aria-label={`Add one ${f.name}`}
                    disabled={f.qty >= f.maxQty}
                    onClick={() => stepFlower(f.itemId, 1)}
                  >
                    +
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <button
          type="button"
          className="button tinted pob-add-btn"
          onClick={() => openPicker("FLOWER")}
        >
          Add flowers
        </button>
      </section>

      <section className="pob-built">
        <div className="pob-built-head">
          <span>Wrap</span>
          <span>{wrapSummary}</span>
        </div>

        {wrapRows.map((p) => {
          const roll = rollById(p.itemId);
          const available = roll?.availableCm ?? p.availableCm;
          return (
            <div key={p.itemId} className="pob-wrap-row">
              <div className="pob-wrap-head">
                <span className="pob-cart-row-name">
                  {p.name}
                  {shortageIds.has(p.itemId) ? (
                    <span className="badge danger pob-cart-badge">short</span>
                  ) : null}
                </span>
                <button
                  type="button"
                  className="button plain pob-wrap-drop"
                  onClick={() => removePaper(p.itemId)}
                >
                  Remove
                </button>
              </div>
              <span className="muted pob-wrap-meta">
                {p.categoryLabel} · {p.cm} cm · {available} cm on the roll
              </span>
              {roll ? (
                <div className="pob-chip-row pob-wrap-lengths">
                  {PAPER_PRESETS.map((preset) => (
                    <button
                      key={preset.value}
                      type="button"
                      aria-pressed={p.cm === preset.cm}
                      disabled={preset.cm > available}
                      className={`sf-type-chip${p.cm === preset.cm ? " sf-type-chip--active" : ""}`}
                      onClick={() => setPaper(roll, preset.cm)}
                    >
                      {translate(preset.labelKey)} — {preset.cm} cm
                    </button>
                  ))}
                  <button
                    type="button"
                    aria-pressed={customFor === p.itemId}
                    className={`sf-type-chip${customFor === p.itemId ? " sf-type-chip--active" : ""}`}
                    onClick={() => {
                      setCustomFor(customFor === p.itemId ? null : p.itemId);
                      setCustomCm(String(p.cm));
                    }}
                  >
                    Custom
                  </button>
                </div>
              ) : null}
              {customFor === p.itemId && roll ? (
                <div className="pob-custom-cm">
                  <input
                    type="number"
                    min={1}
                    step="1"
                    max={available}
                    value={customCm}
                    onChange={(e) => setCustomCm(e.target.value)}
                    aria-label={`${p.name} length in cm`}
                  />
                  <button
                    type="button"
                    className="button"
                    onClick={() => {
                      setPaper(roll, Number(customCm));
                      setCustomFor(null);
                    }}
                  >
                    Use {customCm || "…"} cm
                  </button>
                </div>
              ) : null}
            </div>
          );
        })}

        <div className="pob-wrap-adds">
          {WRAP_KINDS.filter((kind) => rollCards.some((r) => r.categoryCode === kind.code)).map(
            (kind) => (
              <button
                key={kind.code}
                type="button"
                className="button tinted pob-add-btn"
                onClick={() => openPicker(kind.code)}
              >
                {wrapRows.some((p) => rollById(p.itemId)?.categoryCode === kind.code)
                  ? `Add another ${kind.one}`
                  : `Add ${kind.one}`}
              </button>
            ),
          )}
        </div>
      </section>

      <section className="pob-built">
        <div className="field">
          <label htmlFor="pob-bouquet-name">Bouquet name (optional)</label>
          <input
            id="pob-bouquet-name"
            value={cart.bouquetName}
            onChange={(e) => onCartChange({ ...cart, bouquetName: e.target.value })}
            placeholder="e.g. Spring Posy"
          />
        </div>
        <div className="field">
          <label htmlFor="pob-bouquet-notes">Notes for the florist (optional)</label>
          <textarea
            id="pob-bouquet-notes"
            rows={2}
            value={cart.bouquetNotes}
            onChange={(e) => onCartChange({ ...cart, bouquetNotes: e.target.value })}
            placeholder="Wrap, occasion, style…"
          />
        </div>

        <div className="pob-totals">
          <span>
            Suggested price {eur(suggested.totalEur)}
            {suggested.missingPrices ? " (some prices missing)" : ""}
          </span>
          <div className="field pob-total-field">
            <label htmlFor="pob-total">Agreed bouquet price €</label>
            <input
              id="pob-total"
              type="number"
              min={0}
              step="0.01"
              value={cart.bouquetPriceEur}
              onChange={(e) => onCartChange({ ...cart, bouquetPriceEur: e.target.value })}
              placeholder={suggested.totalEur > 0 ? suggested.totalEur.toFixed(2) : "0.00"}
            />
          </div>
        </div>
      </section>


      {pickerOpen ? (
        <div className="pob-sheet-backdrop" role="dialog" aria-label="Add to the bouquet">
          <div className="pob-sheet pob-picker-sheet">
            <div className="pob-picker-head">
              <h3 className="pob-sheet-title">
                {pickerKind === "FLOWER"
                  ? "Add flowers"
                  : `Choose ${(WRAP_KINDS.find((k) => k.code === pickerKind)?.one ?? "roll")}`}
              </h3>
              <button
                type="button"
                className="button plain"
                onClick={() => setPickerOpen(false)}
              >
                Done
              </button>
            </div>

            <input
              type="search"
              className="pob-search"
              placeholder="Search by name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search items"
            />

            {pickerKind === "FLOWER" ? (
              <>
                <div className="pob-chip-row" aria-label="Color filter">
                  <button
                    type="button"
                    aria-pressed={colorId === ""}
                    className={`sf-color-chip${colorId === "" ? " sf-color-chip--active" : ""}`}
                    onClick={() => setColorId("")}
                  >
                    All
                  </button>
                  {colors.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      aria-pressed={colorId === c.id}
                      className={`sf-color-chip${colorId === c.id ? " sf-color-chip--active" : ""}`}
                      onClick={() => setColorId(colorId === c.id ? "" : c.id)}
                    >
                      <span
                        className="pob-color-dot"
                        style={{ background: c.hex ?? "#bbb" }}
                        aria-hidden="true"
                      />
                      {c.name}
                    </button>
                  ))}
                </div>

                {subcategories.length > 0 ? (
                  <div className="pob-chip-row" aria-label="Flower type">
                    <button
                      type="button"
                      aria-pressed={subCategoryId === ""}
                      className={`sf-type-chip${subCategoryId === "" ? " sf-type-chip--active" : ""}`}
                      onClick={() => setSubCategoryId("")}
                    >
                      All
                    </button>
                    {subcategories.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        aria-pressed={subCategoryId === s.id}
                        className={`sf-type-chip${subCategoryId === s.id ? " sf-type-chip--active" : ""}`}
                        onClick={() => setSubCategoryId(subCategoryId === s.id ? "" : s.id)}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </>
            ) : null}

            <div className="pob-picker-list">
              {pickerKind === "FLOWER" ? (
                visibleFlowers.length === 0 ? (
                  <div className="empty-state">Nothing here matches your filters.</div>
                ) : (
                  <div className="pob-grid">
                    {visibleFlowers.map((it) => {
                      const inCart = qtyInCart(it.itemId);
                      const stock = Number(it.qtyOnHand);
                      const capped = inCart >= stock;
                      return (
                        <button
                          key={it.itemId}
                          type="button"
                          className={`pob-card${inCart > 0 ? " pob-card--selected" : ""}`}
                          onClick={() => addFlower(it)}
                          disabled={stock <= 0}
                          aria-label={`Add ${it.name}`}
                        >
                          <div className="pob-card-photo">
                            {it.color ? (
                              <span
                                className="pob-color-dot pob-card-dot"
                                style={{ background: it.color.hex ?? "#bbb" }}
                                title={it.color.name}
                                aria-hidden="true"
                              />
                            ) : null}
                            {inCart > 0 ? (
                              <span className="pob-card-count">{inCart}</span>
                            ) : null}
                            <ItemThumb
                              itemId={it.itemId}
                              imageKey={it.imageKey}
                              alt={it.name}
                              size={92}
                            />
                          </div>
                          <span className="pob-card-name">{it.name}</span>
                          <span className="pob-card-price">
                            {it.defaultSaleUnitPriceEur != null
                              ? `${eur(Number(it.defaultSaleUnitPriceEur))}/stem`
                              : "Price missing"}
                          </span>
                          <span className="pob-card-stock">
                            {capped ? `only ${it.qtyOnHand} left` : `${it.qtyOnHand} in stock`}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )
              ) : visibleRolls.length === 0 ? (
                <div className="empty-state">No rolls in stock for this category.</div>
              ) : (
                <div className="pob-grid">
                  {visibleRolls.map((r) => {
                    const chosen = cart.papers.find((p) => p.itemId === r.itemId);
                    return (
                      <button
                        key={r.itemId}
                        type="button"
                        className={`pob-card${chosen ? " pob-card--selected" : ""}`}
                        onClick={() => addRoll(r)}
                        aria-label={`Add ${r.name}`}
                      >
                        <div className="pob-card-photo">
                          {r.color ? (
                            <span
                              className="pob-color-dot pob-card-dot"
                              style={{ background: r.color.hex ?? "#bbb" }}
                              title={r.color.name}
                              aria-hidden="true"
                            />
                          ) : null}
                          {chosen ? (
                            <span className="pob-card-count">{chosen.cm} cm</span>
                          ) : null}
                          <ItemThumb
                            itemId={r.itemId}
                            imageKey={r.imageKey}
                            alt={r.name}
                            size={92}
                          />
                        </div>
                        <span className="pob-card-name">{r.name}</span>
                        <span className="pob-card-price">{r.categoryLabel}</span>
                        <span className="pob-card-stock">{r.availableCm} cm in stock</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <div className="pob-cart-bar">
        <span className="pob-cart-bar-summary">
          {stems} {stems === 1 ? "stem" : "stems"} · {pieces}{" "}
          {pieces === 1 ? "piece" : "pieces"}
          {!empty ? ` · ${eur(suggested.totalEur)}${suggested.missingPrices ? "+" : ""}` : ""}
        </span>
        <button type="button" className="button plain" onClick={onSkip}>
          Skip bouquet
        </button>
        <button type="button" className="button" disabled={empty} onClick={onContinue}>
          Continue
        </button>
      </div>
    </div>
  );
}
